import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Network } from 'lucide-react';
import { get, post, put } from '@/lib/api';
import { useBranchLock } from '@/lib/branch';
import { describeError } from '@/lib/auth';
import { humanize } from '@/lib/utils';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { RecordState } from '@/components/ui/record-state';
import { Alert, Field, Input, Select } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { DEPARTMENT_KINDS, orgReturnTo, type Branch, type Department } from './org-types';

export function DepartmentPage() {
  const { departmentId = '' } = useParams();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), orgReturnTo('departments'));

  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: () => get<Branch[]>('/org/branches'),
  });
  const departments = useQuery({
    queryKey: ['departments'],
    queryFn: () => get<Department[]>('/org/departments'),
    enabled: Boolean(departmentId),
  });
  const department = departments.data?.find((row) => row.id === departmentId) ?? null;

  if (!branches.data || (departmentId && !department)) {
    return (
      <FormScreen title={departmentId ? tr('admin', 'loading') : tr('admin', 'add_a_department')} icon={Network} backTo={returnTo}>
        <RecordState error={branches.error ?? departments.error} missing={Boolean(departments.data)} />
      </FormScreen>
    );
  }

  return <DepartmentForm key={departmentId || 'new'} department={department} branches={branches.data} returnTo={returnTo} />;
}

function DepartmentForm({ department, branches, returnTo }: { department: Department | null; branches: Branch[]; returnTo: string }) {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const lock = useBranchLock();
  const [initial] = useState(() => ({
    branchId: department?.branchId ?? (lock.locked ? lock.branchId : branches.length === 1 ? branches[0].id : ''),
    code: department?.code ?? '',
    name: department?.name ?? '',
    kind: department?.kind ?? 'clinical',
  }));
  const [form, setForm] = useState(initial);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => {
      const payload = {
        branchId: form.branchId,
        code: form.code.toUpperCase(),
        name: form.name,
        kind: form.kind,
        isActive: department?.isActive ?? true,
      };
      return department ? put(`/org/departments/${department.id}`, payload) : post('/org/departments', payload);
    },
    onSuccess: () => {
      toast.success(department ? tr('admin', 'department_updated') : tr('admin', 'department_created'));
      void queryClient.invalidateQueries({ queryKey: ['departments'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const ready = Boolean(form.branchId && form.code && form.name);
  const dirty = !create.isSuccess && JSON.stringify(form) !== JSON.stringify(initial);

  return (
    <FormScreen
      title={department ? tr('admin', 'edit_x', { name: department.name }) : tr('admin', 'add_a_department')}
      icon={Network}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        setError(null);
        create.mutate();
      }}
    >
      <FormSection title={tr('admin', 'department')} icon={Network}>
        <Field label={tr('admin', 'branch')} required className="sm:col-span-2">
          <Select value={form.branchId} disabled={Boolean(department)} readOnly={lock.locked} onChange={(e) => setForm({ ...form, branchId: e.target.value })}>
            <option value="">{tr('admin', 'select_a_branch')}</option>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={tr('admin', 'code')} required>
          <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} maxLength={16} />
        </Field>
        <Field label={tr('admin', 'kind')} required>
          <ChoiceButtons
            layout="chips"
            value={form.kind}
            onChange={(kind) => setForm({ ...form, kind })}
            required
            options={DEPARTMENT_KINDS.map((k) => ({ value: k, label: humanize(k) }))}
          />
        </Field>
        <Field label={tr('admin', 'name')} required className="sm:col-span-2">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions
        submitLabel={department ? tr('admin', 'save_changes') : tr('admin', 'create_department')}
        loading={create.isPending}
        incomplete={!ready}
        cancelTo={returnTo}
      />
    </FormScreen>
  );
}
